import { tooltipFooterCb } from '@/components/Charts/callbacks';
import { htmlLegendPlugin } from '@/components/Charts/plugins';
import { CircularProgress } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import classNames from 'classnames';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';

interface CustomChartOptions extends ChartOptions<'doughnut'> {
  borderRadius?: number;
  plugins?: {
    htmlLegend?: { containerID: string };
  } & ChartOptions<'doughnut'>['plugins'];
}

interface IProps {
  chartData: ChartData<'doughnut'>;
  loading: boolean;
  legendContainerId: string;
  hideTooltipValue?: boolean;
}

export const DoughnutChart = ({ chartData, loading, legendContainerId, hideTooltipValue }: IProps) => {
  const footerCb = useMemo(() => tooltipFooterCb({ fractionDigits: 1 }), []);
  const tooltipLabel = useMemo(() => (hideTooltipValue ? () => '' : undefined), [hideTooltipValue]);
  const legendPlugin = useMemo(() => htmlLegendPlugin<'doughnut'>({ fractionDigits: 1 }), []);

  const options: CustomChartOptions = useMemo(
    () => ({
      responsive: true,
      borderRadius: 8,
      cutout: 75,
      spacing: chartData.labels?.length === 1 ? 0 : 2,
      plugins: {
        datalabels: {
          display: false,
        },
        htmlLegend: {
          variant: 'attribution',
          containerID: legendContainerId,
        },
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            footer: footerCb,
            label: tooltipLabel,
          },
        },
      },
    }),
    [chartData, legendContainerId, footerCb, tooltipLabel],
  );

  return (
    <div className='flex h-full'>
      <div className={classNames('text-lg w-full flex justify-center print:my-[20px]', { 'my-[auto]': !loading })}>
        {loading ? (
          <div className='flex w-full justify-center mt-32'>
            <CircularProgress />
          </div>
        ) : (
          <div className='w-[230px] h-[230px] print:w-[180px] print:h-[180px]'>
            <Doughnut options={options} plugins={[legendPlugin]} data={chartData} />
          </div>
        )}
      </div>
      <div
        id={legendContainerId}
        className={classNames(
          'text-[#757575] text-sm font-semibold flex flex-wrap gap-[15px] justify-center print:justify-start items-center w-full',
          { hidden: loading },
        )}
      />
    </div>
  );
};

export default DoughnutChart;
